import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useCheckDepositHistoryData } from 'features/rdc/hooks/useCheckDepositHistoryData';
import { CHECK_DEPOSIT_HISTORY } from 'features/rdc/screens/CheckDepositHistory/CheckDepositHistoryScreen.constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

export default function CheckDepositHistoryScreen() {
  const router = useRouter();
  const { loadingState, response, error, refresh, loadMore } =
    useCheckDepositHistoryData();
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const { bottom } = useSafeAreaInsets();
  const isLoading = loadingState === 'loading';

  const { t: translatedText } = useTranslation('rdc', {
    keyPrefix: 'rdcHistoryEmptyState',
  });

  const handleActionOnPress = () => {
    router.push({
      pathname: '/move-money/check-deposit',
    });
  };
  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      {response?.length ? (
        <SectionedContentScreen
          contentContainerStyle={{ paddingBottom: bottom }}
          initialNumToRender={CHECK_DEPOSIT_HISTORY.pageSize}
          model={{ sections: response }}
          onEndReached={loadMore}
          onEndReachedThreshold={2}
          refreshing={loadingState === 'refreshing'}
          onRefresh={() => refresh && refresh(true)}
          maskCellsFromAmplitude
        />
      ) : (
        <View style={[styles.container, styles.emptyStateScreenContainer]}>
          <Stack.Screen
            options={{
              title: translatedText('title'),
              headerShown: true,
              headerBackVisible: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar {...props} />
              ),
            }}
          />
          <EmptyStateScreen
            headlineText={translatedText('noHistoryTitle')}
            bodyText={translatedText('noHistoryMessage')}
            illustrationKey="CHECK_A"
            actionButtonText={translatedText('buttonLabel')}
            analyticsId="mobileCheckDepositClicked"
            customAttributes={{ source_name: 'mobile_deposit_history' }}
            actionOnPress={handleActionOnPress}
          />
        </View>
      )}
      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              tryAgainAction={() => refresh?.(false)}
            />
          ) : null
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.tertiary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  emptyStateScreenContainer: {
    backgroundColor: theme.color.background.primary,
  },
});
